import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Goal } from "../types";
import { getGoals, updateGoal, deleteGoal } from "../repository/daysRepository";
import GoalForm from "./GoalForm";

const GoalsList: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadGoals = useCallback(async () => {
    try {
      const data = await getGoals();
      setGoals(data);
    } catch (err) {
      console.error("Failed to load goals:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const handleStatusChange = async (goal: Goal, newStatus: "active" | "paused" | "archived") => {
    try {
      await updateGoal(goal.uuid, { status: newStatus } as Partial<Goal>);
      loadGoals();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleDelete = async (goalUuid: string) => {
    try {
      await deleteGoal(goalUuid);
      setConfirmDelete(null);
      loadGoals();
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const handleFormSaved = () => {
    setShowForm(false);
    setEditingGoal(undefined);
    loadGoals();
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (showForm) {
    return (
      <GoalForm
        existingGoal={editingGoal}
        onSaved={handleFormSaved}
        onCancel={() => { setShowForm(false); setEditingGoal(undefined); }}
      />
    );
  }

  const active = goals.filter((g) => g.status === "active");
  const paused = goals.filter((g) => g.status === "paused");
  const archived = goals.filter((g) => g.status === "archived");

  const renderGoalRow = (goal: Goal) => (
    <div key={goal.uuid} className="goal-row">
      <div className="goal-row-info" onClick={() => navigate(`/goals/${goal.uuid}/history`)}>
        <span className="goal-row-title">{goal.title}</span>
        <span className="goal-row-meta">
          {goal.goal_type === "numeric" && goal.target && `target: ${goal.target} · `}
          {goal.frequency_days > 1 ? `every ${goal.frequency_days} days` : "daily"}
        </span>
      </div>
      <div className="goal-row-actions">
        {goal.status !== "active" && (
          <button className="btn-small" onClick={() => handleStatusChange(goal, "active")}>Activate</button>
        )}
        {goal.status === "active" && (
          <button className="btn-small" onClick={() => handleStatusChange(goal, "paused")}>Pause</button>
        )}
        {goal.status !== "archived" && (
          <button className="btn-small" onClick={() => handleStatusChange(goal, "archived")}>Archive</button>
        )}
        <button
          className="btn-small"
          onClick={() => { setEditingGoal(goal); setShowForm(true); }}
        >
          Edit
        </button>
        {confirmDelete === goal.uuid ? (
          <>
            <button className="btn-small btn-danger" onClick={() => handleDelete(goal.uuid)}>Confirm</button>
            <button className="btn-small" onClick={() => setConfirmDelete(null)}>Cancel</button>
          </>
        ) : (
          <button className="btn-small btn-danger" onClick={() => setConfirmDelete(goal.uuid)}>Delete</button>
        )}
      </div>
    </div>
  );

  return (
    <div className="goals-page">
      <div className="goals-header">
        <h2>Goals</h2>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          + New Goal
        </button>
      </div>

      {goals.length === 0 && (
        <div className="empty-state">
          <p>No goals yet. Create your first goal to get started.</p>
        </div>
      )}

      {active.length > 0 && (
        <section className="goals-section">
          <h3 className="section-label">Active</h3>
          {active.map(renderGoalRow)}
        </section>
      )}

      {paused.length > 0 && (
        <section className="goals-section">
          <h3 className="section-label">Paused</h3>
          {paused.map(renderGoalRow)}
        </section>
      )}

      {archived.length > 0 && (
        <section className="goals-section">
          <h3 className="section-label">Archived</h3>
          {archived.map(renderGoalRow)}
        </section>
      )}
    </div>
  );
};

export default GoalsList;
